import React, { useEffect, useState, useRef } from 'react';
import { AnalyticalTable, Button, ObjectStatus, Dialog, Title, Select, Option, BusyIndicator, DatePicker, Input, Icon, FlexBox, FlexBoxJustifyContent, MessageStrip, Text, Label, CheckBox } from '@ui5/webcomponents-react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../..//utils/axiosInstance';
import Layout from '../Layout';
import { formatNumberWithComma, rightAlignedNumberStyle } from '../../utils/validator';
import useAutoTranslate from '../../hooks/useAutoTranslate';

// Helper to format date to YYYY-MM-DDTHH:mm:ss.sssZ
// Adjust time part as needed (e.g., specific time, start/end of day)
const formatToISODateTime = (dateString, time = "00:00:00.000Z") => {
    if (!dateString) return null;
    try {
        // Assuming dateString is YYYY-MM-DD or parseable by Date
        const date = new Date(dateString);
        // Ensure it's a valid date
        if (isNaN(date.getTime())) return null;
        // Get parts (use UTC methods to avoid timezone issues if necessary)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        // Combine date and time (ensure time is valid format)
        return `${year}-${month}-${day}T${time}`;
    } catch (e) {
        console.error("Error formatting date:", e);
        return null;
    }
};

const CreateAssignment = () => {
  const [data, setData] = useState([]);
  const [virtualRampData, setVirtualRampData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedTotalWeight, setSelectedTotalWeight] = useState(0);
  const [selectedTotalVolume, setSelectedTotalVolume] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [searchParams, setSearchParams] = useState({
    asnNumber: '',
    purchaseOrderNumber: '',
    startDate: null,
    endDate: null,
    asnStatus: 'Received' // Default value set to 'Received'
  });
  
  const [dateError, setDateError] = useState('');
  
  // Estado para controlar si se está buscando - Initialize based on default filter
  const [isSearching, setIsSearching] = useState(!!searchParams.asnStatus);
  
  // Estado para controlar si debemos ejecutar la búsqueda
  const [shouldSearch, setShouldSearch] = useState(false);

  // --- State for Error Dialog ---
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  // --- End Error Dialog State ---

  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState({});
  const [warehouseDetails, setWarehouseDetails] = useState(null);

  const { autoT } = useAutoTranslate();

  // Function to fetch Virtual Ramp data
  const fetchVirtualRampData = async (rampId) => {
    if (!rampId) return;
    try {
      const response = await axiosInstance.get(`virtual-ramps/${rampId}`);
      console.log("Rampa", response.data.data);
      if (response.data.code === "SUCCESS") {
        setVirtualRampData(response.data.data);
        // Fetch warehouse details if warehouseId exists
        if (response.data.data.warehouseId) {
          await fetchWarehouseDetails(response.data.data.warehouseId);
        }
      } else {
        setError(prevError => `${prevError ? prevError + '; ' : ''}Error fetching virtual ramp: ${response.data.records.message}`);
      }
    } catch (err) {
      setError(prevError => `${prevError ? prevError + '; ' : ''}Error fetching virtual ramp data`);
      console.error('Error fetching Virtual Ramp data:', err);
    }
  };

  // Initial data load useEffect - Modified to fetch both ASN and Ramp data
  useEffect(() => {
    setLoading(true); // Set loading true at the start
    const initialFetch = async () => {
      await fetchVirtualRampData(id); // Fetch ramp data first or concurrently

      if (searchParams.asnStatus) { // Check if default status is set for ASNs
        await handleAdvancedSearch(); // Perform ASN search with default status
      } else {
        await fetchData(1); // Fetch ASNs for page 1 if no default
      }
      setLoading(false); // Set loading false after all fetches complete
    };
    initialFetch();
  }, [id]); // Depend on id to refetch ramp data if it changes

  // Pagination useEffect - Fetch based on isSearching state
  useEffect(() => {
    // Avoid running on initial mount (handled above) if currentPage is 1 initially
    if (currentPage > 1 && !isSearching) {
      fetchData(currentPage);
    }
  }, [currentPage, isSearching]); // Also depend on isSearching

  // useEffect to trigger search on param changes (debounced) - Should remain functional
  useEffect(() => {
    // Avoid running on initial mount if shouldSearch is initially false
    if (shouldSearch && !dateError) {
      const debounceTimeout = setTimeout(() => {
        const hasSearchParams = searchParams.asnNumber || searchParams.purchaseOrderNumber || searchParams.asnStatus || (searchParams.startDate && searchParams.endDate);
        setIsSearching(hasSearchParams);
        handleAdvancedSearch();
        setShouldSearch(false);
      }, 500);

      return () => clearTimeout(debounceTimeout);
    }
  }, [shouldSearch, dateError, searchParams]);

  const fetchData = async (page = 1) => {
    try {
      const url = `asn-documents?page=${page}&size=30`;
      const response = await axiosInstance.get(url);

      if (response.data.records.code === "SUCCESS") {
        const fetchedAsns = response.data.records.data || [];
        // Filter out assigned ASNs before setting state
        const unassignedAsns = fetchedAsns.filter(asn => asn.rampAssignmentStatus !== "ASSIGNED");
        setData(unassignedAsns);
        console.log("Datos ASN (No Asignados)", unassignedAsns);
        setError(null);
      } else {
        setError(prevError => `${prevError ? prevError + '; ' : ''}Error fetching ASNs: ${response.data.records.message}`);
        setData([]); // Clear data on error
      }
    } catch (err) {
      setError(prevError => `${prevError ? prevError + '; ' : ''}Error fetching ASN data`);
      setData([]); // Clear data on error
      console.error('Error fetching ASN data:', err);
    }
    // Note: setLoading is handled in the initial useEffect
  };

  const handleAdvancedSearch = async () => {
    try {
      console.log('=== handleAdvancedSearch ===');
      console.log('Estado actual de searchParams:', searchParams);
      setIsSearching(true);

      const params = new URLSearchParams();
      if (searchParams.asnNumber) params.append('asnNumber', searchParams.asnNumber);
      if (searchParams.purchaseOrderNumber) params.append('purchaseOrderNumber', searchParams.purchaseOrderNumber);
      if (searchParams.asnStatus) params.append('asnStatus', searchParams.asnStatus);

      const hasSearchParams = params.toString() || (searchParams.startDate && searchParams.endDate);

      if (hasSearchParams) {
        const url = `asn-documents/search?${params.toString()}&page=0&size=50`;
        console.log("URL de búsqueda ASN:", url);
        const response = await axiosInstance.get(url);
        console.log("Search Response Data:", response.data.records)

        if (response.data.records.code === "SUCCESS") {
          const searchedAsns = response.data.records.data || [];
           // Filter out assigned ASNs before setting state
          const unassignedAsns = searchedAsns.filter(asn => asn.rampAssignmentStatus !== "ASSIGNED");
          setData(unassignedAsns);
          console.log('Búsqueda Notificación exitosa (No Asignados)', unassignedAsns);
          setError(null);
        } else {
          setError(prevError => `${prevError ? prevError + '; ' : ''}Error searching Notificaciones: ${response.data.records.message}`);
          setData([]);
        }
      } else {
        // No search parameters, fetch default unassigned data (page 1)
        setIsSearching(false);
        await fetchData(1); // fetchData already filters
      }
    } catch (err) {
      console.error("Error en búsqueda avanzada Notificacion:", err);
      setError(prevError => `${prevError ? prevError + '; ' : ''}Error en la búsqueda avanzada Notificacion`);
      setData([]);
    }
     // Note: setLoading is handled in the initial useEffect
  };

  const handleSearchInputChange = (field, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value
    }));
    
    // Solo ejecutamos la búsqueda inmediatamente para campos que no son fechas
    if (field !== 'startDate' && field !== 'endDate') {
      setShouldSearch(true);
    }
  };

  const handleStartDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        // Si se limpia la fecha
        setSearchParams((prev) => ({
          ...prev,
          startDate: null
        }));
        setDateError('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newStartDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newStartDate = new Date(`${year}-${month}-${day}T00:00:00`);
        } else {
          newStartDate = new Date(dateValue);
        }
      } catch (error) {
        newStartDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newStartDate.getTime())) {
        console.error("Fecha de inicio inválida:", dateValue);
        setDateError('Fecha de inicio inválida');
        return;
      }
      
      // Validación: Si hay fecha de fin, verificar que la fecha de inicio no sea posterior
      if (searchParams.endDate) {
        const endDate = new Date(searchParams.endDate);
        if (newStartDate > endDate) {
          setDateError('La fecha de inicio no puede ser posterior a la fecha de fin');
          return;
        }
      }
      
      setDateError('');
      setSearchParams((prev) => ({
        ...prev,
        startDate: newStartDate
      }));
      
      // Solo activamos la búsqueda si ya tenemos ambas fechas
      if (searchParams.endDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error("Error al procesar fecha de inicio:", error);
      setDateError('Error al procesar la fecha de inicio');
    }
  };

  const handleEndDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        // Si se limpia la fecha
        setSearchParams((prev) => ({
          ...prev,
          endDate: null
        }));
        setDateError('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newEndDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newEndDate = new Date(`${year}-${month}-${day}T23:59:59`);
        } else {
          newEndDate = new Date(dateValue);
        }
      } catch (error) {
        newEndDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newEndDate.getTime())) {
        console.error("Fecha de fin inválida:", dateValue);
        setDateError('Fecha de fin inválida');
        return;
      }
      
      // Validación: Si hay fecha de inicio, verificar que la fecha de fin no sea anterior
      if (searchParams.startDate) {
        const startDate = new Date(searchParams.startDate);
        if (newEndDate < startDate) {
          setDateError('La fecha de fin no puede ser anterior a la fecha de inicio');
          return;
        }
      }
      
      setDateError('');
      setSearchParams((prev) => ({
        ...prev,
        endDate: newEndDate
      }));
      
      // Solo activamos la búsqueda si ya tenemos ambas fechas
      if (searchParams.startDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error("Error al procesar fecha de fin:", error);
      setDateError('Error al procesar la fecha de fin');
    }
  };
  const handleResetSearch = () => {
    setSearchParams({
      asnNumber: '',
      purchaseOrderNumber: '',
      startDate: null,
      endDate: null,
      asnStatus: '' // Clear the status filter
    });
    setDateError('');
    setIsSearching(false); // Turn off search mode
    setCurrentPage(1); // Go back to page 1
    fetchData(1); // Fetch the first page of unfiltered data
  };

  useEffect(() => {
    const filterAndSortData = () => {
      let sortedData = [...data].sort((a, b) => 
        new Date(b.asnDate) - new Date(a.asnDate)
      );

      // Filter ASNs based on warehouse name and client address match
      if (warehouseDetails?.name) {
        sortedData = sortedData.filter(asn => {
          const poDetails = purchaseOrderDetails[asn.purchaseOrderId];
          if (!poDetails?.clientAddress) return false;
          
          // Convert both strings to lowercase for case-insensitive comparison
          const warehouseName = warehouseDetails.name.toLowerCase();
          const clientAddress = poDetails.clientAddress.toLowerCase();
          
          // Check if warehouse name is included in client address
          const isMatch = clientAddress.includes(warehouseName);
          
          console.log(`Comparing warehouse: ${warehouseName} with address: ${clientAddress} - Match: ${isMatch}`);
          
          return isMatch;
        });
      }

      setFilteredData(sortedData);
    };

    filterAndSortData();
  }, [data, warehouseDetails, purchaseOrderDetails]);
  
  const userDataString = localStorage.getItem('userData');

  // Paso 2: Parsear la cadena JSON a un objeto JavaScript
  const userData = JSON.parse(userDataString);

  // Paso 3: Acceder a la propiedad 'id' del objeto
  const CreatedBy = userData.id;

  const handleSort = (event) => {
    console.log('handleSort called with event:', event);
    const { column, sortBy } = event.detail;
    console.log('Sort details:', { column, sortBy });

    if (!column || !sortBy) {
      console.log('No column or sortBy provided, returning');
      return;
    }

    const sortedData = [...data].sort((a, b) => {
      console.log('Comparing values:', {
        a: a[column.accessor],
        b: b[column.accessor],
        sortBy
      });

      if (sortBy === 'asc') {
        return a[column.accessor] > b[column.accessor] ? 1 : -1;
      } else {
        return a[column.accessor] < b[column.accessor] ? 1 : -1;
      }
    });

    console.log('Sorted data:', sortedData);
    setData(sortedData);
  };

  // Function to handle toggling selection for a single row
  const handleRowSelectionToggle = (asnId) => {
    setSelectedRows(prevSelected => {
      const newSelected = new Set(prevSelected); // Clone the set
      if (newSelected.has(asnId)) {
        newSelected.delete(asnId); // Deselect
      } else {
        newSelected.add(asnId); // Select
      }
      console.log('Updated Selected IDs:', newSelected);
      return newSelected;
    });
  };

  // useEffect to recalculate totals when selection changes
  useEffect(() => {
    let weightKgSum = 0; // Accumulate in KG first for clarity
    let volumeSum = 0;

    // Iterate over the current data to find selected items
    data.forEach(asn => {
      // Check if the ASN's ID is in the selectedRows Set
      if (selectedRows.has(asn.id)) {
        // Parse weight (assuming it's in KG)
        const weightKg = parseFloat(asn.totalWeight ?? 0);
        // Parse volume (assuming it's already in m³)
        const volume = parseFloat(asn.totalVolume ?? 0);

        if (!isNaN(weightKg)) {
          weightKgSum += weightKg; // Sum in KG
        }
        if (!isNaN(volume)) {
          volumeSum += volume; // Sum volume directly
        }
      }
    });

    // Convert total KG sum to Tons and update state
    setSelectedTotalWeight(weightKgSum);
    setSelectedTotalVolume(volumeSum);

    console.log('Recalculated Weight (kg):', weightKgSum);
    console.log('Recalculated Volume (m³):', volumeSum);

  }, [selectedRows, data]); // Recalculate when selection or the underlying data changes

  const fetchPurchaseOrderDetails = async (orderId) => {
    try {
      const response = await axiosInstance.get(`purchase-orders/${orderId}`);
      console.log("order ",orderId, response.data.data.clientAddress)
      if (response.data.code === "SUCCESS") {
        setPurchaseOrderDetails(prev => ({
          ...prev,
          [orderId]: response.data.data
        }));
      }
    } catch (error) {
      console.error(`Error fetching purchase order details for ID ${orderId}:`, error);
    }
  };

  useEffect(() => {
    const fetchPODetails = async () => {
      for (const asn of data) {
        if (asn.purchaseOrderId && !purchaseOrderDetails[asn.purchaseOrderId]) {
          await fetchPurchaseOrderDetails(asn.purchaseOrderId);
        }
      }
    };
    
    if (data.length > 0) {
      fetchPODetails();
    }
  }, [data]);

  // Add function to fetch warehouse details
  const fetchWarehouseDetails = async (warehouseId) => {
    try {
      const response = await axiosInstance.get(`warehouses/${warehouseId}`);
      console.log("Warehouse details:", response.data.data);
      if (response.data.code === "SUCCESS") {
        setWarehouseDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching warehouse details:", error);
    }
  };

  const columns = [
    // Custom Selection Column
    {
      id: 'selection',
      Header: '',
      width: 60,
      hAlign: 'center',
      disableSortBy: true,
      Cell: ({ row }) => {
        const asnId = row.original.id;
        return (
          <CheckBox
            checked={selectedRows.has(asnId)}
            onChange={() => handleRowSelectionToggle(asnId)}
          />
        );
      },
    },
    {
      Header: 'Número Notificación',
      accessor: 'asnNumber',
      sortable: true,
      hAlign: 'center'
    },
    {
      Header: 'Orden de compra',
      accessor: 'purchaseOrderId',
      sortable: true,
      hAlign: 'center',
      Cell: ({ value }) => {
        const poDetails = purchaseOrderDetails[value] || {};
        return (
          <div>
            <div>OC: {poDetails.orderNumber || 'Cargando...'}</div>
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              {poDetails.supplier?.supplierName || ''}
            </div>
          </div>
        );
      }
    },
    {
      Header: 'Fecha Entrega',
      accessor: 'deliveryDate',
      sortable: true,
      hAlign: 'center',
      Cell: ({ value }) => {
        const date = new Date(value);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      },
      disableFilters: true,
    },
    {
      Header: 'Peso (kg)',
      accessor: 'totalWeight',
      sortable: true,
      hAlign: 'center',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
      )
    },
    {
      Header: 'Volumen (m³)',
      accessor: 'totalVolume',
      sortable: true,
      hAlign: 'center',
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
      )
    },
    {
      Header: 'Tipo de bulto',
      accessor: 'chargeType',
      sortable: true,
      hAlign: 'center'
    },
    {
      Header: 'Numero de OC',
      accessor: 'purchaseOrderNumber',
      sortable: true,
      hAlign: 'center'
    },
    {
      Header: 'Bodega Actual',
      accessor: 'position',
      sortable: true,
      hAlign: 'center'
    }
  ];

  const handleNextPage = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
  };

  // Función auxiliar para formatear fechas de manera segura
  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      // Formato dd/MM/yyyy
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return '';
    }
  };

  // Function to display errors in the dialog
  const showErrorDialog = (message) => {
    setErrorDialogMessage(message);
    setIsErrorDialogOpen(true);
  };

  // Function to handle saving the assignment (POST or PUT)
  const handleSaveAssignment = async () => {
    // Clear previous specific save error and close dialog if open
    setSaveError(null);
    setIsErrorDialogOpen(false);
    setErrorDialogMessage('');

    // --- Initial Validations ---
    if (!id) {
        showErrorDialog(autoT("No se encontró el ID de la rampa virtual."));
        return;
    }
    if (selectedRows.size === 0) {
        showErrorDialog(autoT("Por favor, seleccione al menos una Notificación para guardar."));
        return;
    }
    if (!virtualRampData) {
        showErrorDialog(autoT("No se pudieron cargar los datos de la rampa para validar la capacidad."));
        return;
    }

    // --- Date Validation ---
    const selectedAsnDetails = data.filter(asn => selectedRows.has(asn.id));
    if (selectedAsnDetails.length !== selectedRows.size) {
        showErrorDialog(autoT("Error al obtener detalles de las notificaciones seleccionados.")); return;
    }
    
    // Remove the following block to eliminate the date validation
    /*
    let commonDeliveryDate = null;
    if (selectedAsnDetails.length > 0) {
        commonDeliveryDate = selectedAsnDetails[0].deliveryDate;
        for (let i = 1; i < selectedAsnDetails.length; i++) {
            if (selectedAsnDetails[i].deliveryDate !== commonDeliveryDate) {
                showErrorDialog("Las fechas de entrega de las notificaciones seleccionados deben ser iguales."); return;
            }
        }
    }
    */

    // Continue with the rest of the function...
    // Set scheduledArrivalTimeISO and scheduledDepartureTimeISO based on the first selected ASN's delivery date
    const scheduledArrivalTimeISO = formatToISODateTime(selectedAsnDetails[0].deliveryDate, "00:00:00.000Z");
    const scheduledDepartureTimeISO = formatToISODateTime(selectedAsnDetails[0].deliveryDate, "23:59:59.000Z");

    // --- Construct Payload ---
    const basePayload = {
        driverName: null,
        driverPhone: null,
        driverDocumentNumber: null,
        scheduledArrivalTime: scheduledArrivalTimeISO,
        scheduledDepartureTime: scheduledDepartureTimeISO,
        actualArrivalTime: null,
        actualDepartureTime: null,
        totalAssignedWeightTons: selectedTotalWeight,
        totalAssignedVolumeCubicMeters: selectedTotalVolume,
        totalAssignedPallets: 0,
        notes: null,
        cancellationReason: null,
        status: "PENDING",
        virtualRampId: id,
        asnDocumentIds: Array.from(selectedRows),
        createdBy: CreatedBy
    };

    // --- Determine Request Details ---
    const requestMethod = 'post';
    const requestUrl = 'virtual-ramp-assignments';

    console.log(`Intentando ${requestMethod.toUpperCase()} en ${requestUrl} con payload:`, basePayload);

    // --- Execute Request ---
    try {
        let response;
        response = await axiosInstance.post(requestUrl, basePayload);

        // --- Handle Response ---
        if (response.status === 200 || response.status === 201 || response.status === 204 || response.data?.code === "SUCCESS") {
            // --- SUCCESS ---
            console.log(`Asignación creada exitosamente.`, response.data);
            setSelectedRows(new Set()); // Clear selection

            // Update the virtual ramp status to "OCCUPIED"
            await updateVirtualRampStatus(id, "OCCUPIED");

            // Navigate silently on success
            navigate(`/ramp/${id}`);
        } else {
            console.log(response)
            // --- API Error on Save ---
            const errorMessage = response.data?.message || "Ocurrió un error al guardar la asignación.";
            console.error("Error al guardar (API):", errorMessage, response);
            setSaveError(`Error al guardar: ${errorMessage}`); // Keep this to show in MessageStrip potentially
            showErrorDialog(`Error al guardar: ${errorMessage}`); // Show in Dialog
        }
    } catch (err) {
        // --- Network/Server Error on Save ---
        console.error(`Error durante ${requestMethod.toUpperCase()}:`, err);
        const message = err.response?.data?.message || err.message || "Ocurrió un error de red o del servidor.";
        setSaveError(`Error al guardar: ${message}`); // Keep this
        showErrorDialog(`Error al guardar: ${message}`); // Show in Dialog
    } finally {
        setSaving(false);
    }
  };

  // Function to update the virtual ramp status
  const updateVirtualRampStatus = async (rampId, status) => {
    try {
        const payload = { status }; // Create payload with the new status
        const response = await axiosInstance.put(`virtual-ramps/${rampId}`, payload); // Make the PUT request
        if (response.status === 200 || response.data?.code === "SUCCESS") {
            console.log(`Estado de la rampa virtual actualizado a ${status}.`);
        } else {
            console.error(`Error al actualizar el estado de la rampa: ${response.data?.message}`);
        }
    } catch (error) {
        console.error(`Error al actualizar el estado de la rampa:`, error);
    }
  };

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <BusyIndicator active="true" size="L" text={autoT("Cargando datos...")} />
    </div>
  );
  if (error && !virtualRampData && !data.length) return <div>Error: {error}</div>;

  return (
    <Layout>
      {/* --- Error Dialog --- */}
      <Dialog
        open={isErrorDialogOpen}
        headerText={autoT("Error de Validación")} // Or a more general title like "Error"
        onAfterClose={() => setIsErrorDialogOpen(false)} // Close dialog handler
        footer={
          <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ width: '100%', paddingRight: '1rem', paddingBottom: '0.5rem' }}>
            <Button onClick={() => setIsErrorDialogOpen(false)}>{autoT("Cerrar")}</Button>
          </FlexBox>
        }
      >
        <div style={{ padding: '20px' }}>
          <Text>{errorDialogMessage}</Text>
        </div>
      </Dialog>
      {/* --- End Error Dialog --- */}

      {/* Container for Ramp Details - Modified to include warehouse */}
      {virtualRampData && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Title level="H3" style={{ marginBottom: '1rem', color: '#333', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem' }}>
            {autoT("Detalles de la Rampa Virtual y Selección Actual")}
          </Title>
          <FlexBox wrap="Wrap" style={{ gap: '1.5rem' }}>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Label style={{ fontWeight: 'bold' }}>{autoT("Nombre Rampa")}:</Label>
              <Text>{virtualRampData.name || 'N/A'}</Text>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Label style={{ fontWeight: 'bold' }}>{autoT("Código Rampa")}:</Label>
              <Text>{virtualRampData.rampCode || 'N/A'}</Text>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Label style={{ fontWeight: 'bold' }}>{autoT("Bodega destino")}:</Label>
              <Text>{warehouseDetails?.name || 'Cargando...'}</Text>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Label style={{ fontWeight: 'bold' }}>{autoT("Cap. Peso (kg)")}:</Label>
              <Text>{formatNumberWithComma(virtualRampData.maxWeightCapacityTons) ?? '0'}</Text>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}>
              <Label style={{ fontWeight: 'bold' }}>{autoT("Cap. Volumen (m³)")}:</Label>
              <Text>{formatNumberWithComma(virtualRampData.maxVolumeCapacityCubicMeters) ?? '0'}</Text>
            </FlexBox>

            {/* Divider (Optional) */}
            <div style={{ width: '100%', borderTop: '1px dashed #ccc', margin: '0.5rem 0' }}></div>

            {/* Selected Totals */}
            <FlexBox alignItems="Center" style={{ gap: '0.5rem', color: '#0854A0' }}> {/* Style totals differently */}
                <Label style={{ fontWeight: 'bold' }}>{autoT("Peso Seleccionado (kg)")}:</Label>
                <Text>{formatNumberWithComma(selectedTotalWeight)}</Text>
            </FlexBox>
            <FlexBox alignItems="Center" style={{ gap: '0.5rem', color: '#0854A0' }}> {/* Style totals differently */}
                <Label style={{ fontWeight: 'bold' }}>{autoT("Volumen Seleccionado (m³)")}:</Label>
                <Text>{formatNumberWithComma(selectedTotalVolume)}</Text>
            </FlexBox>
          </FlexBox>
        </div>
      )}

      {/* Display General Fetch Error Messages if any */}
      {error && !saveError && ( // Avoid showing general error if a save error is already shown
        <MessageStrip design="Negative" style={{ marginBottom: '15px' }}>
          {autoT(error)}
        </MessageStrip>
      )}

      <div style={{
        width: '100%',
        padding: '10px',
        paddingTop: 0, // Remove extra padding top if details section exists
        overflow: 'visible'
      }}>
        
        {/* Búsqueda avanzada siempre visible */}
        <div style={{ 
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '15px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {autoT("Número Notificación")}
              </label>
              <Input 
                value={searchParams.asnNumber}
                onChange={(e) => handleSearchInputChange('asnNumber', e.target.value)}
                placeholder={autoT("Ingrese número ASN")}
                style={{ width: '100%' }}
              />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {autoT("Número OC")}
              </label>
              <Input 
                value={searchParams.purchaseOrderNumber}
                onChange={(e) => handleSearchInputChange('purchaseOrderNumber', e.target.value)}
                placeholder={autoT("Ingrese número OC")}
                style={{ width: '100%' }}
              />
            </div>
            <Button 
              design="Transparent" 
              onClick={handleResetSearch}
              style={{ color: '#0854A0' }}
            >
              {autoT("Limpiar filtros")}
            </Button>
          </div>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: '10px',
            alignItems: 'flex-end'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {autoT("Fecha Inicio")}
              </label>
              <DatePicker 
                value={searchParams.startDate ? formatDate(searchParams.startDate) : ''}
                onChange={handleStartDateChange}
                style={{ width: '100%' }}
                formatPattern="dd/MM/yyyy"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {autoT("Fecha Fin")}
              </label>
              <DatePicker 
                value={searchParams.endDate ? formatDate(searchParams.endDate) : ''}
                onChange={handleEndDateChange}
                style={{ width: '100%' }}
                formatPattern="dd/MM/yyyy"
              />
            </div>
          </div>
          
          {dateError && (
            <MessageStrip 
              design="Negative" 
              style={{ marginTop: '10px' }}
            >
              {autoT(dateError)}
            </MessageStrip>
          )}
          
          
          {searchParams.startDate && !searchParams.endDate && (
            <MessageStrip 
              design="Warning" 
              style={{ marginTop: '10px' }}
            >
              {autoT("Se requiere fecha de fin para buscar por fechas")}
            </MessageStrip>
          )}
          
          {!searchParams.startDate && searchParams.endDate && (
            <MessageStrip 
              design="Warning" 
              style={{ marginTop: '10px' }}
            >
              {autoT("Se requiere fecha de inicio para buscar por fechas")}
            </MessageStrip>
          )}
        </div>
        
        <div
  style={{
    width: `${columns.length * 200}px`, // Ajusta el ancho según el número de columnas (150px por columna, ajustable)
    overflow: 'visible',
  }}
>
  <AnalyticalTable
    data={filteredData}
    columns={columns}
    minRows={filteredData.length > 0 ? filteredData.length : 10}
    visibleRows={filteredData.length > 0 ? filteredData.length : 10}
    style={{
      width: '90%',
      height: `${Math.max(filteredData.length > 0 ? filteredData.length : 10, 10) * 55}px`, // Ensure minimum height
      '--sapList_HeaderBackground': '#f5f6f7',
      '--sapList_BorderColor': '#e5e5e5',
    }}
    onSort={handleSort}
    sortable={true}
    header
    alternateRowColor
  />
</div>
        <div style={{ 
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          padding: '1rem',
          marginTop: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
          borderTop: '1px solid #e5e5e5'
        }}>
          <Button onClick={handlePreviousPage} disabled={currentPage === 1}>← {autoT("Anterior")}</Button>
          <span style={{ flex: 1, textAlign: 'center' }}>{autoT("Página")} {currentPage}</span>

             <Button
                 design="Emphasized"
                 icon="save"
                 onClick={handleSaveAssignment}
                 disabled={saving || selectedRows.size === 0}
             >
                 {saving ? autoT("Guardando...") : autoT("Guardar Asignación")}
             </Button>
          <Button onClick={handleNextPage} style={{ marginLeft: '20px' }}>{autoT("Siguiente")} →</Button>
        </div>
      </div>
    </Layout>
  );
};

export default CreateAssignment;